import { define } from './define';

// Keys start with "review.". Spanish first; English must have the same keys (TypeScript checks it).
// The English side keeps the wording the end-to-end runs look for ("Comment here", "Post comment", "Approve…", "Page 1 of 3"…).
export const review = define({
  es: {
    // header
    'review.crumbs.label': 'Dónde estás',
    'review.crumbs.pieces': 'Piezas',
    'review.meta.ai': 'Generada con IA',
    'review.versions.label': 'Versiones de esta variante',
    'review.compare.with': 'Comparar con',
    'review.compare.earlier': 'Anterior',
    'review.compare.later': 'Posterior',
    'review.compare.stop': 'Dejar de comparar',
    'review.compare.thisOne': 'V{n} (esta)',
    'review.compare.imageAlt': '{label}: {name}',
    'review.compare.nothing': 'Nada en esta posición',
    'review.compare.playBoth': 'Reproducir las dos',
    'review.compare.pauseBoth': 'Pausar las dos',
    'review.compare.modeGroup': 'Cómo comparar',
    'review.compare.side': 'Lado a lado',
    'review.compare.flip': 'Alternar',
    'review.compare.showing': 'Viendo {label}',
    'review.compare.position': 'Posición',

    // stage
    'review.stage.empty': 'Esta versión no tiene nada que enseñar.',
    'review.media.fullscreen': 'Pantalla completa',
    'review.media.exitFullscreen': 'Salir de la pantalla completa',
    'review.pin.open': 'Abrir el comentario {n}',
    'review.pdf.error': 'No se ha podido abrir el PDF: {error}',
    'review.pdf.page': 'Página {n} del PDF',
    'review.zone.label': 'Zona segura',
    'review.zone.hint': 'Sombrea lo que tapa la interfaz de cada red: nombre, texto y botones',
    'review.zone.prompt': 'Ver lo que tapa la red',
    'review.zone.off': 'No sombrear nada',
    'review.video.play': 'Reproducir',
    'review.video.pause': 'Pausar',
    'review.video.prevFrame': 'Fotograma anterior',
    'review.video.nextFrame': 'Fotograma siguiente',
    'review.video.prevFrameHint': 'Fotograma anterior ( , )',
    'review.video.nextFrameHint': 'Fotograma siguiente ( . )',
    'review.video.frameHint': 'Fotograma {n}, a {fps} fotogramas por segundo',
    'review.video.commentHere': 'Comentar aquí',
    'review.video.setEnd': 'Terminar aquí',
    'review.video.setEndHint': 'Convierte el comentario en un tramo que acaba en este momento',
    'review.video.mute': 'Silenciar',
    'review.video.unmute': 'Activar el sonido',
    'review.timeline.label': 'Línea de tiempo',
    'review.timeline.marker': 'Comentario {n} en {time}',
    'review.pager.pdfGroup': 'Páginas del PDF',
    'review.pager.itemGroup': 'Imágenes del carrusel',
    'review.pager.prev': 'Página anterior',
    'review.pager.next': 'Página siguiente',
    'review.pager.page': 'Página {n} de {count}',
    'review.pager.item': 'Imagen {n} de {count}',
    'review.pager.goToPage': 'Ir a la página {n}',
    'review.pager.goToItem': 'Ir a la imagen {n}',

    // subtitles
    'review.subs.title': 'Subtítulos',
    'review.subs.lines': { one: '{count} línea', other: '{count} líneas' },
    'review.subs.file': 'Archivo de subtítulos',
    'review.subs.problem': '{name}: {problem}.',
    'review.subs.skipped': {
      one: 'Un bloque del archivo no se ha podido leer y no se muestra.',
      other: '{count} bloques del archivo no se han podido leer y no se muestran.',
    },
    'review.subs.truncated': 'El archivo es muy largo: solo se muestran sus primeras líneas.',
    'review.subs.goTo': 'Ir a esta línea',
    'review.subs.noWords': '(sin texto)',
    'review.subs.commentsOnLine': { one: '{count} comentario en esta línea', other: '{count} comentarios en esta línea' },
    'review.subs.commentLine': 'Comentar la línea {n}',
    'review.subs.comment': 'Comentar',

    // decision
    'review.side.label': 'Decisión y comentarios',
    'review.decision.unknown': 'alguien',
    'review.decision.requestChanges': 'Pedir cambios',
    'review.decision.approve': 'Aprobar…',
    'review.decision.reject': 'Rechazar…',
    'review.decision.isAuthor': 'Esta versión la subiste tú, así que tiene que aprobarla otra persona.',
    'review.decision.alreadyApproved': 'Ya aprobaste esta versión.',
    'review.decision.alreadyRejected': 'Ya rechazaste esta versión.',
    'review.decision.toPiece': 'Ir a la pieza',
    'review.decision.approvedFor': { one: 'Aprobada para {count} cuenta', other: 'Aprobada para {count} cuentas' },
    'review.decision.schedule': 'Programar…',
    'review.decision.toLatest': 'Ir a la V{n}',
    'review.decision.more': 'Ver más',
    'review.decision.less': 'Ver menos',

    // approve, reject and request changes
    'review.approve.title': 'Aprobar la versión {n}',
    'review.approve.tied': 'Tu aprobación queda ligada a estos archivos exactos y a este texto: si cambia un solo byte o una palabra, deja de contar.',
    'review.text.label': 'Texto de la publicación',
    'review.text.none': 'Esta versión no trae texto.',
    'review.facts.stale': 'Se planificó con un conjunto anterior de datos del negocio. Ahora está aprobada la revisión {n}; revisa el texto con ella.',
    'review.facts.open': 'Ver los datos del negocio',
    'review.approve.needs': { one: 'Esta marca necesita {count} aprobador.', other: 'Esta marca necesita {count} aprobadores.' },
    'review.approve.openBlock': {
      one: 'Queda {count} comentario abierto. Resuélvelo antes de aprobar.',
      other: 'Quedan {count} comentarios abiertos. Resuélvelos antes de aprobar.',
    },
    'review.approve.seeComments': 'Ver los comentarios',
    'review.approve.accounts': 'Aprobar para estas cuentas',
    'review.approve.noAccounts': 'Todavía no hay cuentas. Las añade un administrador en Ajustes.',
    'review.approve.checklist': 'Comprobaciones',
    'review.approve.note': 'Nota (opcional)',
    'review.approve.submit': 'Aprobar',
    'review.approve.done': 'Aprobada',
    'review.reject.title': 'Rechazar la versión {n}',
    'review.reject.lead': 'El productor recibe esta versión de vuelta para cambiarla. Cuéntale por qué.',
    'review.reject.reason': 'Motivo',
    'review.reject.submit': 'Rechazar',
    'review.reject.done': 'Rechazada: le hemos pedido cambios al productor',
    'review.changes.title': 'Pedir cambios',
    'review.changes.withOpen': {
      one: 'El comentario abierto vuelve al productor con la versión.',
      other: 'Los {count} comentarios abiertos vuelven al productor con la versión.',
    },
    'review.changes.noneOpen': 'Todavía no hay comentarios abiertos: escribe aquí qué hay que cambiar, o cierra esto y comenta en el sitio exacto.',
    'review.changes.noteOptional': 'Nota general (opcional)',
    'review.changes.what': '¿Qué hay que cambiar?',
    'review.changes.submit': 'Pedir cambios',
    'review.changes.done': 'Cambios pedidos',

    // tabs and details
    'review.tabs.label': 'Panel de revisión',
    'review.tabs.comments': 'Comentarios',
    'review.tabs.details': 'Detalles',
    'review.details.approvals': 'Decisiones',
    'review.details.noDecisions': 'Nadie ha decidido todavía.',
    'review.details.approved': 'Aprobó',
    'review.details.rejected': 'Rechazó',
    'review.details.changedSince': 'los archivos cambiaron después',
    'review.details.fingerprint': 'Huella',
    'review.details.fingerprintHint': 'Un resumen de todos los archivos de esta versión. Las aprobaciones van ligadas a él: si cambia un solo byte, dejan de contar.',
    'review.details.files': 'Archivos',
    'review.details.brief': 'Encargo',
    'review.asset.video': 'Vídeo',
    'review.asset.image': 'Imagen',
    'review.asset.pdf': 'PDF',
    'review.asset.subtitles': 'Subtítulos',
    'review.asset.cover': 'Portada',

    // comments
    'review.anchor.general': 'General',
    'review.anchor.cue': 'Subtítulo {n} · {time}',
    'review.anchor.point': 'Pág. {page} · punto',
    'review.anchor.page': 'Pág. {page}',
    'review.anchor.area': 'Pág. {page} · zona',
    'review.list.resolved': 'Resueltos ({count})',
    'review.list.nothingOpen': 'Nada abierto.',
    'review.thread.number': 'Comentario {n}',
    'review.thread.goTo': 'Ir a este sitio',
    'review.thread.resolve': 'Resolver',
    'review.thread.reopen': 'Reabrir',
    'review.thread.peopleOnly': 'solo personas',
    'review.thread.peopleOnlyHint': 'El agente deja en paz los comentarios solo para personas: no los responde ni los resuelve',
    'review.thread.makePeopleOnly': 'Solo personas',
    'review.thread.letAgent': 'Que lo vea el agente',
    'review.thread.carried': 'sigue abierto desde la v{n}',
    'review.thread.carriedHint': 'Se abrió en una versión anterior y sigue abierto',
    'review.thread.resolvedIn': 'resuelto en la v{n}',
    'review.thread.resolvedBy': 'resuelto por {name}',
    'review.thread.resolved': 'resuelto',
    'review.thread.frameAlt': 'El fotograma al que apunta este comentario',
    'review.thread.frameZoom': 'Ver el fotograma más grande',
    'review.thread.frameTitle': 'Fotograma',
    'review.reply.label': 'Respuesta',
    'review.reply.placeholder': 'Responder…',
    'review.reply.typeLabel': 'Tipo de respuesta',
    'review.reply.send': 'Enviar',
    'review.reply.kind.plain': 'Solo responder',
    'review.reply.kind.fixed': 'Hecho',
    'review.reply.kind.cannotDo': 'No se puede',
    'review.reply.kind.needsHuman': 'Necesita a una persona',
    'review.compose.label': 'Comentario',
    'review.compose.placeholderImage': 'Pulsa en la imagen o arrastra sobre una zona para comentar ahí…',
    'review.compose.placeholderPdf': 'Pulsa en la página o arrastra sobre una zona para comentar ahí…',
    'review.compose.placeholderGeneral': 'Escribe un comentario general…',
    'review.compose.placeholderAnchored': '¿Qué hay que cambiar aquí?',
    'review.compose.removeAnchor': 'Quitar el sitio',
    'review.compose.peopleOnly': 'Solo personas',
    'review.compose.peopleOnlyHint': '· el agente no lo responde, no lo resuelve ni actúa sobre él',
    'review.compose.post': 'Publicar comentario',
    'review.compose.postHint': 'Publicar comentario (Intro)',
    'review.compose.retrying': 'Reintentando…',
    'review.compose.closed': 'Esta versión ya no admite comentarios nuevos.',

    // short times, filters, shortcuts
    'review.ago.now': 'ahora',
    'review.ago.min': '{n} min',
    'review.ago.h': '{n} h',
    'review.ago.d': '{n} d',
    'review.ago.justNow': 'hace un momento',
    'review.ago.phrase': 'hace {when}',
    'review.filter.label': 'Qué comentarios ver',
    'review.filter.all': 'Todos',
    'review.filter.open': 'Abiertos',
    'review.filter.resolved': 'Resueltos',
    'review.filter.mine': 'Míos',
    'review.list.nothing': 'Todavía no hay comentarios en esta versión.',
    'review.list.nothingResolved': 'Nada resuelto todavía.',
    'review.list.nothingMine': 'No has comentado nada en esta versión.',
    'review.thread.reply': 'Responder',
    'review.compose.momentHint': 'El comentario va a este momento del vídeo. Pulsa para que sea general.',
    'review.compose.makeGeneral': 'Que sea general',
    'review.compose.toMoment': 'Pulsa para llevarlo al momento del vídeo',
    'review.keys.title': 'Atajos de teclado',
    'review.keys.space': 'Espacio',
    'review.keys.shift': 'Mayús',
    'review.keys.enter': 'Intro',
    'review.keys.play': 'Reproducir o pausar',
    'review.keys.frame': 'Un fotograma atrás o adelante',
    'review.keys.second': 'Un segundo atrás o adelante',
    'review.keys.comment': 'Comentar en este momento',
    'review.keys.cancel': 'Cancelar la marca o cerrar',
    'review.keys.send': 'Enviar el comentario',
    'review.keys.newline': 'Otra línea en el comentario',
    'review.keys.page': 'Página anterior o siguiente',
    'review.video.commentHereHint': 'Comentar en este momento (C)',
    'review.tabs.subtitles': 'Subtítulos',
    'review.details.version': 'Esta versión',
    'review.back': 'Volver a la pieza',
    'review.stage.label': 'La pieza',
    'review.compare.comparing': 'Comparando con V{n}',
    'review.compare.curtain': 'Cortinilla: arrástrala para ver una versión u otra',
    'review.compare.pick': 'Elegir con qué versión comparar',
    'review.compare.wipe': 'Cortinilla',
    'review.compare.withN': 'Comparar con V{n}',
    'review.compose.drawing': 'Dibujo ({count})',
    'review.compose.drawingHint': 'Este dibujo va con el comentario',
    'review.compose.placeholderMoment': 'Comentar en este momento…',
    'review.decision.approvalsOf': 'Lleva {done} de las {count} aprobaciones que necesita.',
    'review.decision.copyLink': 'Copiar el enlace',
    'review.decision.menu': 'Más acciones',
    'review.decision.uploadNew': 'Subir versión nueva',
    'review.draw.arrow': 'Flecha',
    'review.draw.clear': 'Borrar el dibujo',
    'review.draw.colour': 'Color',
    'review.draw.colour.yellow': 'Amarillo',
    'review.draw.colour.red': 'Rojo',
    'review.draw.colour.green': 'Verde',
    'review.draw.colour.blue': 'Azul',
    'review.draw.colour.white': 'Blanco',
    'review.draw.label': 'Dibujar sobre el fotograma',
    'review.draw.pen': 'Lápiz',
    'review.draw.rect': 'Rectángulo',
    'review.draw.toggle': 'Dibujar',
    'review.draw.undo': 'Deshacer el último trazo',
    'review.filter.agent': 'Del agente',
    'review.filter.allComments': 'Todos los comentarios',
    'review.filter.clear': 'Quitar los filtros',
    'review.filter.drawn': 'Con dibujo',
    'review.filter.narrow': 'Filtrar por',
    'review.filter.people': 'Personas',
    'review.filter.search': 'Buscar en los comentarios',
    'review.filter.searchPlaceholder': 'Buscar texto o persona…',
    'review.filter.show': 'Mostrar',
    'review.keys.draw': 'Dibujar sobre el fotograma',
    'review.keys.fullscreen': 'Pantalla completa',
    'review.keys.mute': 'Silenciar o activar el sonido',
    'review.left.agent': 'Agente',
    'review.left.changes': 'Qué cambia en la V{n}',
    'review.left.label': 'Variantes y versiones',
    'review.left.noNotes': 'Esta versión no trae notas.',
    'review.left.noVersion': 'Sin versiones',
    'review.left.openCount': { one: '{count} abierto', other: '{count} abiertos' },
    'review.left.resolves': 'Resuelve {done} de {count}',
    'review.left.resolvesHint': 'Ver los comentarios que resuelve',
    'review.left.toggle': 'Mostrar u ocultar las variantes y versiones',
    'review.left.variants': 'Variantes',
    'review.left.versions': 'Versiones',
    'review.list.nothingMatches': 'Ningún comentario coincide con los filtros.',
    'review.people.label': 'Quién ha participado',
    'review.thread.agent': 'Agente',
    'review.thread.drawn': 'Con dibujo',
    'review.thread.frameOf': 'Fotograma de la v{n}',
    'review.thread.openState': 'Abierto',
    'review.uploader.agent': 'Subida por el agente, con el token «{name}» de {owner}',
    'review.uploader.tokenOf': 'token de {owner}',
    'review.uploader.agentAlone': 'Subida por el agente, con el token «{name}»',
    'review.versions.button': 'Versión {n}: cambiar de versión',
    'review.video.speed': 'Velocidad: {speed}',
    'review.video.speedHint': 'Cambiar la velocidad',
  },
  en: {
    // header
    'review.crumbs.label': 'Breadcrumb',
    'review.crumbs.pieces': 'Pieces',
    'review.meta.ai': 'Generated with AI',
    'review.versions.label': 'Versions of this variant',
    'review.compare.with': 'Compare with',
    'review.compare.earlier': 'Earlier',
    'review.compare.later': 'Later',
    'review.compare.stop': 'Stop comparing',
    'review.compare.thisOne': 'V{n} (this one)',
    'review.compare.imageAlt': '{label}: {name}',
    'review.compare.nothing': 'Nothing at this position',
    'review.compare.playBoth': 'Play both',
    'review.compare.pauseBoth': 'Pause both',
    'review.compare.modeGroup': 'How to compare',
    'review.compare.side': 'Side by side',
    'review.compare.flip': 'Flip',
    'review.compare.showing': 'Showing {label}',
    'review.compare.position': 'Position',

    // stage
    'review.stage.empty': 'This version has nothing to show.',
    'review.media.fullscreen': 'Full screen',
    'review.media.exitFullscreen': 'Exit full screen',
    'review.pin.open': 'Open comment {n}',
    'review.pdf.error': 'Could not open the PDF: {error}',
    'review.pdf.page': 'PDF page {n}',
    'review.zone.label': 'Safe area',
    'review.zone.hint': "Shades what each network's interface covers: name, caption and buttons",
    'review.zone.prompt': 'What the network covers',
    'review.zone.off': 'Shade nothing',
    'review.video.play': 'Play',
    'review.video.pause': 'Pause',
    'review.video.prevFrame': 'Previous frame',
    'review.video.nextFrame': 'Next frame',
    'review.video.prevFrameHint': 'Previous frame ( , )',
    'review.video.nextFrameHint': 'Next frame ( . )',
    'review.video.frameHint': 'Frame {n}, at {fps} frames per second',
    'review.video.commentHere': 'Comment here',
    'review.video.setEnd': 'End here',
    'review.video.setEndHint': 'Turn the comment into a span ending at the current time',
    'review.video.mute': 'Mute',
    'review.video.unmute': 'Unmute',
    'review.timeline.label': 'Timeline',
    'review.timeline.marker': 'Comment {n} at {time}',
    'review.pager.pdfGroup': 'PDF pages',
    'review.pager.itemGroup': 'Carousel items',
    'review.pager.prev': 'Previous page',
    'review.pager.next': 'Next page',
    'review.pager.page': 'Page {n} of {count}',
    'review.pager.item': 'Item {n} of {count}',
    'review.pager.goToPage': 'Go to page {n}',
    'review.pager.goToItem': 'Go to item {n}',

    // subtitles
    'review.subs.title': 'Subtitles',
    'review.subs.lines': { one: '{count} line', other: '{count} lines' },
    'review.subs.file': 'Subtitle file',
    'review.subs.problem': '{name}: {problem}.',
    'review.subs.skipped': {
      one: '{count} block of the file could not be read and is not shown.',
      other: '{count} blocks of the file could not be read and are not shown.',
    },
    'review.subs.truncated': 'Only the first lines of this long file are shown.',
    'review.subs.goTo': 'Go to this line',
    'review.subs.noWords': '(no words)',
    'review.subs.commentsOnLine': { one: '{count} comment on this line', other: '{count} comments on this line' },
    'review.subs.commentLine': 'Comment on line {n}',
    'review.subs.comment': 'Comment',

    // decision
    'review.side.label': 'Decision and comments',
    'review.decision.unknown': 'unknown',
    'review.decision.requestChanges': 'Request changes',
    'review.decision.approve': 'Approve…',
    'review.decision.reject': 'Reject…',
    'review.decision.isAuthor': 'You uploaded this version, so someone else has to approve it.',
    'review.decision.alreadyApproved': 'You already approved this version.',
    'review.decision.alreadyRejected': 'You already rejected this version.',
    'review.decision.toPiece': 'Go to the piece',
    'review.decision.approvedFor': { one: 'Approved for {count} account', other: 'Approved for {count} accounts' },
    'review.decision.schedule': 'Schedule…',
    'review.decision.toLatest': 'Go to V{n}',
    'review.decision.more': 'Show more',
    'review.decision.less': 'Show less',

    // approve, reject and request changes
    'review.approve.title': 'Approve version {n}',
    'review.approve.tied': 'Your approval is tied to these exact files and this text: change a single byte or a word and it stops counting.',
    'review.text.label': 'Text of the post',
    'review.text.none': 'This version has no text.',
    'review.facts.stale': 'Planned with an older set of business facts. Revision {n} is approved now; check the text against it.',
    'review.facts.open': 'See the business facts',
    'review.approve.needs': { one: 'This brand needs {count} approver.', other: 'This brand needs {count} approvers.' },
    'review.approve.openBlock': {
      one: '{count} comment is still open. Resolve it before approving.',
      other: '{count} comments are still open. Resolve them before approving.',
    },
    'review.approve.seeComments': 'See comments',
    'review.approve.accounts': 'Approve for these accounts',
    'review.approve.noAccounts': 'No accounts yet. An admin adds them in Settings.',
    'review.approve.checklist': 'Checklist',
    'review.approve.note': 'Note (optional)',
    'review.approve.submit': 'Approve',
    'review.approve.done': 'Approved',
    'review.reject.title': 'Reject version {n}',
    'review.reject.lead': 'The producer gets this version back for changes. Say why.',
    'review.reject.reason': 'Reason',
    'review.reject.submit': 'Reject',
    'review.reject.done': 'Rejected: the producer has been asked for changes',
    'review.changes.title': 'Request changes',
    'review.changes.withOpen': {
      one: '{count} open comment will go back to the producer with the version.',
      other: '{count} open comments will go back to the producer with the version.',
    },
    'review.changes.noneOpen': 'There are no open comments yet: write one here, or close this and comment on the exact place.',
    'review.changes.noteOptional': 'Add a general note (optional)',
    'review.changes.what': 'What should change?',
    'review.changes.submit': 'Request changes',
    'review.changes.done': 'Changes requested',

    // tabs and details
    'review.tabs.label': 'Review panel',
    'review.tabs.comments': 'Comments',
    'review.tabs.details': 'Details',
    'review.details.approvals': 'Approvals',
    'review.details.noDecisions': 'No decisions yet.',
    'review.details.approved': 'Approved',
    'review.details.rejected': 'Rejected',
    'review.details.changedSince': 'files changed since',
    'review.details.fingerprint': 'Fingerprint',
    'review.details.fingerprintHint': 'A hash of every file in this version. Approvals are tied to it: change a single byte and they stop counting.',
    'review.details.files': 'Files',
    'review.details.brief': 'Brief',
    'review.asset.video': 'Video',
    'review.asset.image': 'Image',
    'review.asset.pdf': 'PDF',
    'review.asset.subtitles': 'Subtitles',
    'review.asset.cover': 'Cover',

    // comments
    'review.anchor.general': 'General',
    'review.anchor.cue': 'Subtitle {n} · {time}',
    'review.anchor.point': 'Page {page} · point',
    'review.anchor.page': 'Page {page}',
    'review.anchor.area': 'Page {page} · area',
    'review.list.resolved': 'Resolved ({count})',
    'review.list.nothingOpen': 'Nothing open.',
    'review.thread.number': 'Comment {n}',
    'review.thread.goTo': 'Go to this place',
    'review.thread.resolve': 'Resolve',
    'review.thread.reopen': 'Reopen',
    'review.thread.peopleOnly': 'only people',
    'review.thread.peopleOnlyHint': 'An agent leaves comments for people only alone: it will not reply to them or resolve them',
    'review.thread.makePeopleOnly': 'Only people',
    'review.thread.letAgent': 'Let the agent see it',
    'review.thread.carried': 'still open from v{n}',
    'review.thread.carriedHint': 'Raised on an earlier version and still open',
    'review.thread.resolvedIn': 'resolved in v{n}',
    'review.thread.resolvedBy': 'resolved by {name}',
    'review.thread.resolved': 'resolved',
    'review.thread.frameAlt': 'Frame this comment points at',
    'review.thread.frameZoom': 'See the frame larger',
    'review.thread.frameTitle': 'Frame',
    'review.reply.label': 'Reply',
    'review.reply.placeholder': 'Reply…',
    'review.reply.typeLabel': 'Reply type',
    'review.reply.send': 'Send',
    'review.reply.kind.plain': 'Just a reply',
    'review.reply.kind.fixed': 'Fixed',
    'review.reply.kind.cannotDo': 'Cannot do',
    'review.reply.kind.needsHuman': 'Needs a person',
    'review.compose.label': 'Comment',
    'review.compose.placeholderImage': 'Click the picture or drag over an area to comment right there…',
    'review.compose.placeholderPdf': 'Click the page or drag over an area to comment right there…',
    'review.compose.placeholderGeneral': 'Write a general comment…',
    'review.compose.placeholderAnchored': 'What should change here?',
    'review.compose.removeAnchor': 'Remove anchor',
    'review.compose.peopleOnly': 'Only people',
    'review.compose.peopleOnlyHint': '· an agent will not reply to this, resolve it or act on it',
    'review.compose.post': 'Post comment',
    'review.compose.postHint': 'Post comment (Enter)',
    'review.compose.retrying': 'Retrying…',
    'review.compose.closed': 'This version no longer takes new comments.',

    // short times, filters, shortcuts
    'review.ago.now': 'now',
    'review.ago.min': '{n} min',
    'review.ago.h': '{n} h',
    'review.ago.d': '{n} d',
    'review.ago.justNow': 'just now',
    'review.ago.phrase': '{when} ago',
    'review.filter.label': 'Which comments to show',
    'review.filter.all': 'All',
    'review.filter.open': 'Open',
    'review.filter.resolved': 'Resolved',
    'review.filter.mine': 'Mine',
    'review.list.nothing': 'No comments on this version yet.',
    'review.list.nothingResolved': 'Nothing resolved yet.',
    'review.list.nothingMine': 'You have not commented on this version.',
    'review.thread.reply': 'Reply',
    'review.compose.momentHint': 'The comment goes to this moment of the video. Click to make it general.',
    'review.compose.makeGeneral': 'Make it general',
    'review.compose.toMoment': 'Click to put it on the current moment of the video',
    'review.keys.title': 'Keyboard shortcuts',
    'review.keys.space': 'Space',
    'review.keys.shift': 'Shift',
    'review.keys.enter': 'Enter',
    'review.keys.play': 'Play or pause',
    'review.keys.frame': 'One frame back or forward',
    'review.keys.second': 'One second back or forward',
    'review.keys.comment': 'Comment on this moment',
    'review.keys.cancel': 'Cancel the mark or close',
    'review.keys.send': 'Send the comment',
    'review.keys.newline': 'New line in the comment',
    'review.keys.page': 'Previous or next page',
    'review.video.commentHereHint': 'Comment on this moment (C)',
    'review.tabs.subtitles': 'Subtitles',
    'review.details.version': 'This version',
    'review.back': 'Back to the piece',
    'review.stage.label': 'The piece',
    'review.compare.comparing': 'Comparing with V{n}',
    'review.compare.curtain': 'Wipe: drag it to see one version or the other',
    'review.compare.pick': 'Choose a version to compare with',
    'review.compare.wipe': 'Wipe',
    'review.compare.withN': 'Compare with V{n}',
    'review.compose.drawing': 'Drawing ({count})',
    'review.compose.drawingHint': 'This drawing goes with the comment',
    'review.compose.placeholderMoment': 'Comment on this moment…',
    'review.decision.approvalsOf': '{done} of the {count} approvals it needs so far.',
    'review.decision.copyLink': 'Copy link',
    'review.decision.menu': 'More actions',
    'review.decision.uploadNew': 'Upload a new version',
    'review.draw.arrow': 'Arrow',
    'review.draw.clear': 'Clear the drawing',
    'review.draw.colour': 'Colour',
    'review.draw.colour.yellow': 'Yellow',
    'review.draw.colour.red': 'Red',
    'review.draw.colour.green': 'Green',
    'review.draw.colour.blue': 'Blue',
    'review.draw.colour.white': 'White',
    'review.draw.label': 'Draw on the frame',
    'review.draw.pen': 'Pen',
    'review.draw.rect': 'Rectangle',
    'review.draw.toggle': 'Draw',
    'review.draw.undo': 'Undo the last stroke',
    'review.filter.agent': 'From the agent',
    'review.filter.allComments': 'All comments',
    'review.filter.clear': 'Clear filters',
    'review.filter.drawn': 'With a drawing',
    'review.filter.narrow': 'Filter by',
    'review.filter.people': 'People',
    'review.filter.search': 'Search comments',
    'review.filter.searchPlaceholder': 'Search text or person…',
    'review.filter.show': 'Show',
    'review.keys.draw': 'Draw on the frame',
    'review.keys.fullscreen': 'Full screen',
    'review.keys.mute': 'Mute or unmute',
    'review.left.agent': 'Agent',
    'review.left.changes': 'What changed in V{n}',
    'review.left.label': 'Variants and versions',
    'review.left.noNotes': 'This version has no notes.',
    'review.left.noVersion': 'No versions',
    'review.left.openCount': { one: '{count} open', other: '{count} open' },
    'review.left.resolves': 'Resolves {done} of {count}',
    'review.left.resolvesHint': 'See the comments it resolves',
    'review.left.toggle': 'Show or hide variants and versions',
    'review.left.variants': 'Variants',
    'review.left.versions': 'Versions',
    'review.list.nothingMatches': 'No comment matches the filters.',
    'review.people.label': 'Who took part',
    'review.thread.agent': 'Agent',
    'review.thread.drawn': 'With a drawing',
    'review.thread.frameOf': 'Frame from v{n}',
    'review.thread.openState': 'Open',
    'review.uploader.agent': 'Uploaded by the agent, with {owner}\'s token “{name}”',
    'review.uploader.tokenOf': '{owner}\'s token',
    'review.uploader.agentAlone': 'Uploaded by the agent, with the token “{name}”',
    'review.versions.button': 'Version {n}: switch version',
    'review.video.speed': 'Speed: {speed}',
    'review.video.speedHint': 'Change the speed',
  },
});
